// Permission key registry — single source of truth for the Role Permissions
// matrix from Permissions 2.0 (docs/PERMISSIONS-2.0.md). Translated from the
// product spreadsheet (~/Downloads/Permissions Matrix - Role Permissions.csv).
//
// Two consumers:
//   1. Workspace creation + backfill migration — seeds role + role_perm rows.
//   2. AuthorizationService — resolves whether a role allows an action.
//
// Owner is a real system role with `yes_locked` on every key. Multiple
// users per workspace can hold it. Workspace creators are auto-assigned
// the Owner role; transfer = adding/removing user_role rows. No separate
// workspace.owner_id column — the role is the single source of truth.
//
// INVITE PERMISSIONS LIVE OUTSIDE THIS REGISTRY. They are per-(inviter_role,
// invitable_role) grants in the `role_invite_grant` join table, so custom
// roles work without registry edits. A user can invite into role T iff:
//   1. They hold some role R where role_invite_grant(R, T) exists, AND
//   2. R.hierarchy >= T.hierarchy (hard ceiling: can't invite above your tier).
// Default seeds on workspace creation:
//   admin           → can invite into {guest, limited_member, member, admin}
//   member          → can invite into {guest, limited_member, member}
//   limited_member  → no invite grants
//   guest           → no invite grants
// Custom roles get a configurable hierarchy on create (Admin picks tier) and
// no invite grants by default.

export type SystemRole = "guest" | "limited_member" | "member" | "admin" | "owner";

/**
 * Cell states for the role × permission matrix. "Locked" variants are
 * system-imposed and not editable in the role editor. "If Created" is an
 * ownership conditional resolved at check time against the actor.
 */
export type CellState =
  | "yes"
  | "no"
  | "yes_locked"
  | "no_locked"
  | "if_shared"
  | "if_created";

export type Category =
  | "collaboration"
  | "manage"
  | "workflows"
  | "create_delete"
  | "custom_fields"
  | "comments"
  | "workspace";

export interface PermissionDefinition {
  /** Dot-notation permission key. */
  key: string;
  /** Group for matrix UI rendering. */
  category: Category;
  /** Human-readable label (matches CSV row). */
  label: string;
  /** Tooltip / spreadsheet description. */
  description: string;
  /** Seeded cell states. */
  defaultByRole: Record<SystemRole, CellState>;
  /** Other keys whose state this depends on. */
  interlocks?: string[];
  /**
   * Cell may take the `if_created` state — surfaces an "Only if created"
   * checkbox beneath the toggle in the role-permissions matrix UI.
   */
  supportsOnlyIfCreated?: boolean;
}

export const SYSTEM_ROLES: Record<SystemRole, true> = {
  guest: true,
  limited_member: true,
  member: true,
  admin: true,
  owner: true,
};

export const SYSTEM_ROLE_ORDER: readonly SystemRole[] = [
  "guest",
  "limited_member",
  "member",
  "admin",
  "owner",
];

/** Display names for seeded roles. Used by workspace seeder. */
export const SYSTEM_ROLE_NAMES: Record<SystemRole, string> = {
  guest: "Guest",
  limited_member: "Limited Member",
  member: "Member",
  admin: "Admin",
  owner: "Owner",
};

/**
 * Short descriptions for the system roles, shown in the permissions
 * settings page's "Roles" header strip. Owner is intentionally omitted —
 * it's a special all-access role and not surfaced alongside the editable
 * tiers.
 */
export const SYSTEM_ROLE_DESCRIPTIONS: Record<SystemRole, string> = {
  guest:
    "External people who only need to see what you share with them. Use for clients reviewing work or partners with read access.",
  limited_member:
    "Internal teammates with restricted scope. Can act on their own work but can't change workflows or invite others.",
  member:
    "Default role for most teammates. Can create, edit, and assign work and collaborate across the workspace.",
  admin:
    "Full control over workspace settings, billing, members, and permissions. Reserve for owners and ops leads.",
  owner: "",
};

/**
 * Hierarchy ordering for the "cannot invite above your perm level" rule.
 * Higher number = higher privilege.
 */
export const SYSTEM_ROLE_HIERARCHY: Record<SystemRole, number> = {
  guest: 1,
  limited_member: 2,
  member: 3,
  admin: 4,
  owner: 5,
};

/** All valid cell states. */
export const CELL_STATES: readonly CellState[] = [
  "yes",
  "no",
  "yes_locked",
  "no_locked",
  "if_shared",
  "if_created",
];

/** A cell state grants the action when its base verdict is positive. */
export function cellGrants(state: CellState): boolean {
  return state === "yes" || state === "yes_locked";
}

/** A cell state is editable in the role editor. */
export function cellEditable(state: CellState): boolean {
  return state !== "yes_locked" && state !== "no_locked";
}

/**
 * Collapse a locked cell state to its editable base form. Locks are a
 * system-role concept — custom roles seed from system-role templates but
 * must never carry locked cells, so seeding paths collapse before writing.
 */
export function collapseLockedState(state: CellState): CellState {
  if (state === "yes_locked") return "yes";
  if (state === "no_locked") return "no";
  return state;
}

// Full matrix translation from
//   ~/Downloads/Permissions Matrix - Role Permissions.csv
//
// Convention notes:
// - `delete.items` uses `if_created` (own-only) where the CSV shows base
//   "Yes" + sub-toggle "only_if_created=TRUE". A standalone "yes" means
//   anyone's items can be deleted.
// - `workspace.importing` is omitted — the CSV row was left blank by
//   product. Add it here once the values are defined.
// - `create.views` is omitted — views as first-class entities are deferred
//   to a follow-up; add this key when that work begins.
export const PERMISSION_REGISTRY: readonly PermissionDefinition[] = Object.freeze([
  // Invite permissions are NOT in this registry — see header comment.
  // role_invite_grant (inviter_role_id, invitable_role_id) is the source of
  // truth for who can invite into which role.

  // ─── MANAGE ACTIONS ───────────────────────────────────────────────────
  {
    key: "manage.users",
    category: "manage",
    label: "Manage Users",
    description:
      "Grants the user permission to view and manage all members and guests in a Workspace. This includes adding and removing users, changing roles, and managing invites.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "manage.teams",
    category: "manage",
    label: "Manage Teams",
    description:
      "Grants the user permission to manage all teams in a Workspace. This includes the ability to add, edit, and remove teams, as well as manage their members.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "manage.edit_workflows",
    category: "workflows",
    label: "Edit Workflows",
    description: "Grants the user the ability to edit workflows and their stages.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "widgets.manage",
    category: "manage",
    label: "Manage Widgets",
    description:
      "Grants the user the ability to edit widget boards — adding, configuring, rearranging, duplicating, and removing cards. Widget layouts are shared across the Workspace; users without this permission see a read-only board.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── CREATE & DELETE ACTIONS ──────────────────────────────────────────
  {
    key: "create.roles",
    category: "manage",
    label: "Create Roles",
    description:
      "Gives the user the ability to create, edit, delete and manage all custom roles. Users with this permission by default have access to all permissions. Only admins can be granted this permission.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no_locked",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "create.workflows",
    category: "workflows",
    label: "Create Workflows",
    description: "Grants the user permissions to create a new workflow.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "delete.workflows",
    category: "workflows",
    label: "Delete Workflows",
    description: "Permits the user to delete Workflows.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "create.items",
    category: "create_delete",
    label: "Create Items",
    description:
      "Permits the user to create new items in workflows where the user has access.",
    defaultByRole: {
      // Guests start at `no` (editable) instead of `no_locked` —
      // admins can flip this on per-workspace if external collaborators
      // need to create items in workflows they've been shared on.
      guest: "no",
      limited_member: "yes",
      member: "yes",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "delete.items",
    category: "create_delete",
    label: "Delete Items",
    description:
      "Delete Projects, Accounts, or Contacts. Optionally, restrict so Users can only delete Projects, Accounts, and Contacts they create.",
    supportsOnlyIfCreated: true,
    defaultByRole: {
      guest: "if_created",
      limited_member: "if_created",
      member: "if_created",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "archive.items",
    category: "create_delete",
    label: "Archive Items",
    description: "Permits the user to archive items.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "yes",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── CUSTOM FIELDS ────────────────────────────────────────────────────
  {
    key: "custom_fields.manage",
    category: "custom_fields",
    label: "Manage Custom Fields",
    description:
      "Gives the user permission to create, edit, and delete Custom Fields.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
    interlocks: ["delete.items"],
  },

  // ─── TRASH ────────────────────────────────────────────────────────────
  // Restore-from-trash, permanent-delete, and "empty trash". Workspace-
  // wide cleanup affecting many items — admin/owner only by default.
  {
    key: "trash.manage",
    category: "workspace",
    label: "Manage Trash",
    description:
      "Restore items from trash, permanently delete trashed items, and empty the trash.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── WORKSPACE INTEGRATIONS ───────────────────────────────────────────
  // Set up / manage third-party integrations (webhooks, etc.). Workspace-
  // config tier. Guest/Limited/Member default `no` (editable, not locked)
  // so an admin can grant it to a lower tier if needed.
  {
    key: "workspace.integrations",
    category: "workspace",
    label: "Workspace Integrations",
    description:
      "Gives the user permission to set up and manage any third-party Workspace integration (e.g. webhooks).",
    defaultByRole: {
      guest: "no",
      limited_member: "no",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── ISSUE TYPES ──────────────────────────────────────────────────────
  // Project/Account/Contact (and any custom item type) templates plus
  // their field layout. Workspace-config tier.
  {
    key: "issue_types.manage",
    category: "workspace",
    label: "Manage Item Types",
    description:
      "Create, edit, delete, and configure the field layout of item types (Projects, Accounts, Contacts, and custom types).",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── REASONS ──────────────────────────────────────────────────────────
  // "Updates" in the UI is the configurable update-reasons list (the
  // catalog of pre-canned reasons surfaced when posting an update on an
  // item). Workspace-config tier — admins and owners only by default.
  {
    key: "reasons.manage",
    category: "workspace",
    label: "Manage Reasons",
    description:
      "Create, edit, and delete the catalog of update reasons used across the workspace.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── AUTOMATIONS ──────────────────────────────────────────────────────
  // Workflow automations are workspace-wide machinery — editing and
  // publishing them is admin/owner only by default. Read access is not gated
  // here (everyone in the workspace can view automations and run history).
  {
    key: "automations.manage",
    category: "workspace",
    label: "Manage Automations",
    description:
      "Create, edit, publish, and delete workspace automations.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── ISSUE SOURCES ────────────────────────────────────────────────────
  // "Sources" in the UI is the configurable lead-source list (the catalog of
  // issue sources selectable on an item). Workspace-config tier — admins and
  // owners only by default.
  {
    key: "issue_sources.manage",
    category: "workspace",
    label: "Manage Sources",
    description:
      "Create, edit, and delete the catalog of lead sources used across the workspace.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── COMMENTS ─────────────────────────────────────────────────────────
  {
    key: "comments.edit_own",
    category: "comments",
    label: "Edit Own Comments",
    description: "Edit comments created by the same user.",
    defaultByRole: {
      guest: "yes",
      limited_member: "yes",
      member: "yes",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "comments.delete_own",
    category: "comments",
    label: "Delete Own Comments",
    description: "Delete comments. Members should generally delete only their own.",
    defaultByRole: {
      guest: "yes",
      limited_member: "yes",
      member: "yes",
      admin: "yes",
      owner: "yes_locked",
    },
  },
]);

export const PERMISSION_BY_KEY: ReadonlyMap<string, PermissionDefinition> = new Map(
  PERMISSION_REGISTRY.map((p) => [p.key, p]),
);

/** Resolve a single (role, key) cell using registry defaults. */
export function defaultCellState(role: SystemRole, key: string): CellState | null {
  const def = PERMISSION_BY_KEY.get(key);
  if (!def) return null;
  return def.defaultByRole[role];
}

// ─── ACCESS LEVELS ──────────────────────────────────────────────────────
// Object-level access levels (workflow / item). View-level (full_edit |
// limited) is deferred along with the view entity itself.

export type ObjectAccessLevel = "full_edit" | "edit" | "comment" | "view";

export const OBJECT_ACCESS_LEVELS: Record<ObjectAccessLevel, true> = {
  full_edit: true,
  edit: true,
  comment: true,
  view: true,
};

/**
 * Per access level, which CRUD capabilities are granted at the object.
 * Source: Permissions Matrix - Access Permissions.csv (Heirarchical Access
 * Views section).
 */
export const ACCESS_LEVEL_CAPS: Record<
  ObjectAccessLevel,
  { view: boolean; comment: boolean; edit: boolean; create: boolean; delete: boolean }
> = {
  full_edit: { view: true, comment: true, edit: true, create: true, delete: true },
  edit: { view: true, comment: true, edit: true, create: true, delete: false },
  comment: { view: true, comment: true, edit: false, create: false, delete: false },
  view: { view: true, comment: false, edit: false, create: false, delete: false },
};

export function accessLevelAllows(
  level: ObjectAccessLevel,
  action: "view" | "comment" | "edit" | "create" | "delete",
): boolean {
  return ACCESS_LEVEL_CAPS[level]?.[action] === true;
}

/**
 * Numeric ranking for ObjectAccessLevel comparisons — higher grants
 * more. Each tier implies the lower ones.
 */
export const ACCESS_LEVEL_RANK: Record<ObjectAccessLevel, number> = {
  view: 1,
  comment: 2,
  edit: 3,
  full_edit: 4,
};

function isObjectAccessLevel(level: string): level is ObjectAccessLevel {
  return level in OBJECT_ACCESS_LEVELS;
}

/** Rank of a level; 0 for unknown levels. */
export function accessLevelRank(level: ObjectAccessLevel | string): number {
  return isObjectAccessLevel(level) ? ACCESS_LEVEL_RANK[level] : 0;
}

/**
 * The most-granting level of the given non-empty list. Used when
 * multiple shares grant the same object and the max should win.
 */
export function highestAccessLevel(levels: ObjectAccessLevel[]): ObjectAccessLevel {
  return levels.reduce((acc, l) => (accessLevelRank(l) > accessLevelRank(acc) ? l : acc));
}

// ─── INVITE GRANT DEFAULTS ─────────────────────────────────────────────
// Seeded into role_invite_grant on workspace creation. Keys are inviter
// roles; values are the set of invitable target roles. Read as:
//   "By default, this inviter can invite users into these target roles."
// Hierarchy ceiling is enforced separately at check time — even if a row
// exists, an inviter with lower hierarchy than the target is rejected.

export const DEFAULT_INVITE_GRANTS: Record<SystemRole, readonly SystemRole[]> = {
  guest: [],
  limited_member: [],
  member: ["guest", "limited_member", "member"],
  admin: ["guest", "limited_member", "member", "admin"],
  owner: ["guest", "limited_member", "member", "admin", "owner"],
};

// ─── INVITE GRANT CELL STATES ──────────────────────────────────────────
// role_invite_grant carries a `state` (cell_state) per (inviter, target)
// pair, exactly like role_perm. Seeded as a full grid on workspace
// creation and on custom-role creation. Resolution rules:
//
//   1. Target outranks inviter (hierarchy ceiling)  → no_locked  (×)
//   2. Inviter is a Guest (guests never invite)      → no_locked  (×)
//   3. Inviter is Admin/Owner (always can, at tier)  → yes_locked (✓)
//   4. Pair is a DEFAULT_INVITE_GRANT                 → yes        (toggle on)
//   5. Otherwise                                      → no         (toggle off)
//
// `*_locked` states aren't editable in the matrix; `yes`/`no` are. Custom
// roles are never locked except by the hierarchy ceiling (rule 1) — they
// have no systemRole, so rules 2–4 don't apply and they default to `no`.

/**
 * Resolve the default invite-grant cell state for an (inviter, target)
 * role pair. Each arg carries the role's hierarchy tier and, for system
 * roles, its SystemRole key (null for custom roles).
 */
export function defaultInviteState(
  inviter: { hierarchy: number; systemRole: SystemRole | null },
  target: { hierarchy: number; systemRole: SystemRole | null },
): CellState {
  if (target.hierarchy > inviter.hierarchy) return "no_locked";
  if (inviter.systemRole === "guest") return "no_locked";
  if (inviter.systemRole === "admin" || inviter.systemRole === "owner") {
    return "yes_locked";
  }
  if (
    inviter.systemRole &&
    target.systemRole &&
    DEFAULT_INVITE_GRANTS[inviter.systemRole].includes(target.systemRole)
  ) {
    return "yes";
  }
  return "no";
}
